import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { FileSignature, Mail, Share2, Sparkles, Upload, Plus, Crown, Loader2 } from 'lucide-react';
import { formatDate, statusColor } from '../lib/utils';

const API = '/api';

export default function Dashboard() {
  const { user } = useAuth();
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/documents/`)
      .then(r => setRecentDocs(r.data.slice(0, 5)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const planColors = {
    free: 'bg-gray-100 text-gray-700',
    pro: 'bg-blue-100 text-blue-700',
    business: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-500">Here's an overview of your account.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${planColors[user?.plan] || planColors.free}`}>
            {user?.plan || 'free'} plan
          </span>
          {user?.plan === 'free' && (
            <Link
              to="/pricing"
              className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Crown className="w-4 h-4" /> Upgrade
            </Link>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { to: '/esign', icon: Upload, label: 'Upload & Sign', bg: 'bg-blue-100', iconColor: 'text-blue-600' },
          { to: '/email-signatures', icon: Mail, label: 'Email Signature', bg: 'bg-purple-100', iconColor: 'text-purple-600' },
          { to: '/social', icon: Share2, label: 'Social Banner', bg: 'bg-pink-100', iconColor: 'text-pink-600' },
          { to: '/ai-images', icon: Sparkles, label: 'AI Image', bg: 'bg-amber-100', iconColor: 'text-amber-600' },
        ].map(a => {
          const Icon = a.icon;
          return (
            <Link
              key={a.to}
              to={a.to}
              className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:shadow-md transition-shadow text-center group"
            >
              <div className={`w-10 h-10 ${a.bg} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <Icon className={`w-5 h-5 ${a.iconColor}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">{a.label}</span>
            </Link>
          );
        })}
      </div>

      {/* Plan usage */}
      {user?.plan === 'free' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-center justify-between">
          <div>
            <span className="font-medium text-amber-800">Free Plan: </span>
            <span className="text-amber-700 text-sm">
              {user.docs_this_month}/3 documents used this month
            </span>
          </div>
          <Link to="/pricing" className="text-sm font-medium text-amber-700 hover:underline">
            Upgrade for unlimited →
          </Link>
        </div>
      )}

      {/* Recent documents */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-blue-600" /> Recent Documents
          </h2>
          <Link to="/esign" className="text-blue-600 text-sm font-medium hover:underline">View all</Link>
        </div>
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
          </div>
        ) : recentDocs.length === 0 ? (
          <div className="text-center py-10">
            <FileSignature className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No documents yet</p>
            <Link
              to="/esign"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Upload your first document
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentDocs.map(doc => (
              <div key={doc.id} className="flex items-center justify-between py-3">
                <div>
                  <Link
                    to={`/esign/editor/${doc.id}`}
                    className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    {doc.filename}
                  </Link>
                  <p className="text-xs text-gray-400">
                    {formatDate(doc.created_at)} · {doc.signed_count}/{doc.signer_count} signed
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor(doc.status)}`}>
                  {doc.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
